import { uniqBy } from 'lodash';

import { SUBMISSION_STATUSES } from '@didaskill/domain/assignments/constants';
import type { SessionsRepository } from '@didaskill/domain/data/repositories';
import { NB_DAYS_BEFORE_INACTIVITY } from '@didaskill/domain/users/constants';
import type { StudentAcademicTranscript } from '@didaskill/domain/users/types';
import dayjs from '@didaskill/services/dayjs';

/** Minimal shape of a student enrollment, matching both StudentCourse and StudentViewFull['courses']. */
type StudentEnrollment = {
  course: { id: string };
  class: { id: string };
};

export const getNumberOfDaysSinceAccessStarted = (
  accessStartDate: string | Date,
  date?: string | Date | number,
) =>
  dayjs(accessStartDate).isValid()
    ? dayjs(date).diff(dayjs(accessStartDate).startOf('day'), 'day')
    : null;

export const makeChartDataRelative = (kpis: { daysSinceRegistration: number; value: number }[]) =>
  uniqBy(kpis, (kpi) => kpi.daysSinceRegistration)
    .sort((a, b) => b.daysSinceRegistration - a.daysSinceRegistration)
    .map(({ daysSinceRegistration, value }) => ({
      x: daysSinceRegistration,
      y: Math.round(value * 100) / 100,
    }));

export const makeChartDataWithDate = (
  kpis: { daysSinceRegistration: number; value: number }[],
  minDate: string,
) =>
  makeChartDataRelative(kpis).map(({ x, y }) => ({
    x: dayjs(minDate).add(x, 'days').toDate().getTime(),
    y,
  }));

export const getTranscriptTeachingUnitAssignmentsCount = (
  teachingUnit: StudentAcademicTranscript['course']['teachingUnits'][0],
  statuses?: SUBMISSION_STATUSES[],
) => {
  const assignments = [
    ...teachingUnit.assignments,
    ...(teachingUnit.lessons || []).flatMap(
      ({ assignments: lessonAssignments }) => lessonAssignments,
    ),
  ];

  return {
    count: assignments.filter(
      ({ submission }) =>
        !statuses?.length || (submission && (statuses || []).includes(submission.status)),
    ).length,
    total: assignments.length,
  };
};

export const getTranscriptAssignmentsCount = (
  transcript?: StudentAcademicTranscript,
  statuses: SUBMISSION_STATUSES[] = Object.values(SUBMISSION_STATUSES),
) =>
  (transcript?.course.teachingUnits || []).reduce<{
    count: number;
    total: number;
  }>(
    (acc, teachingUnit) => {
      const { count, total } = getTranscriptTeachingUnitAssignmentsCount(teachingUnit, statuses);

      return {
        count: acc.count + count,
        total: acc.total + total,
      };
    },
    { count: 0, total: 0 },
  );

/**
 * this method checks that a student logged in less than 28 days ago. It takes the last login date as a parameter
 * @param lastActiveTimestamp
 * @returns {boolean}
 */
export const isStudentActive = (lastActiveTimestamp: string): boolean => {
  const inputDate = dayjs(lastActiveTimestamp);
  const twentyEightDaysAgo = dayjs().subtract(NB_DAYS_BEFORE_INACTIVITY, 'day');

  return inputDate.isSameOrBefore(dayjs()) && inputDate.isAfter(twentyEightDaysAgo);
};

export const getIsFirstPingOfTheDay = (
  session: Awaited<ReturnType<SessionsRepository['setActivePing']>> | null | undefined,
) => session?.isFirstPingOfDay ?? false;

/**
 * Check that a course and class pair matches one of the student enrollments.
 * Guards against ids read from an untrusted source, such as url segments captured on a route
 * that is not course-scoped, which do not designate a real enrollment.
 * @param enrollments - Courses the student is enrolled in.
 * @param courseId - Candidate course id.
 * @param classId - Candidate class id.
 * @returns True when the student is enrolled in this course and class.
 */
export const isStudentEnrolledInClass = (
  enrollments: StudentEnrollment[],
  courseId?: string,
  classId?: string,
): boolean => {
  if (!courseId || !classId) {
    return false;
  }

  return enrollments.some(
    (enrollment) => enrollment.course.id === courseId && enrollment.class.id === classId,
  );
};
